import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { Megaphone, Plus, X, Sparkles, CreditCard, Pause, Play, Trash2 } from "lucide-react";

const STATUS_LABELS = {
  brouillon: "Brouillon (non payée)",
  active: "Active",
  en_pause: "En pause",
  suspendue: "Suspendue par la modération",
  terminee: "Terminée (budget épuisé)",
};
const STATUS_STYLES = {
  brouillon: "bg-slate-100 text-slate-600",
  active: "bg-green-50 text-green-700",
  en_pause: "bg-amber-50 text-amber-700",
  suspendue: "bg-red-50 text-red-600",
  terminee: "bg-slate-100 text-slate-500",
};
const PLACEMENT_LABELS = { network: "Fil Réseau", market: "Marché", jobs: "Offres d'emploi" };

const CampaignDialog = ({ onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [placements, setPlacements] = useState(["network"]);
  const [budget, setBudget] = useState("50");
  const [goal, setGoal] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNote, setSuggestNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const togglePlacement = (p) =>
    setPlacements((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const suggest = async () => {
    setError("");
    setSuggestNote("");
    setSuggesting(true);
    try {
      const res = await api.post("/ads/suggest", { goal, context: `Lien : ${linkUrl}` });
      if (res.data.draft?.title) setTitle(res.data.draft.title);
      if (res.data.draft?.body) setBody(res.data.draft.body);
      setSuggestNote("Brouillon IA appliqué — relisez et ajustez avant de payer.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSuggesting(false);
    }
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const res = await api.post("/ads/campaigns", {
        title, body, link_url: linkUrl, placements, budget: parseFloat(budget),
      });
      onSaved(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  const impressions = Math.round((parseFloat(budget) || 0) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-brand-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="campaign-form-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-brand-navy">Nouvelle campagne</h2>
          <button data-testid="campaign-form-close" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-brand-navy">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div data-testid="campaign-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          {suggestNote && (
            <div data-testid="campaign-suggest-note" className="text-sm text-brand-navy bg-brand-turquoise/10 border border-brand-turquoise/30 rounded-lg px-4 py-3">{suggestNote}</div>
          )}
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-sm font-medium text-brand-navy">Objectif (pour l'IA)</label>
              <button
                data-testid="campaign-ai-suggest-button"
                onClick={suggest}
                disabled={suggesting || !goal.trim()}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-turquoise hover:text-brand-navy disabled:opacity-40"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {suggesting ? "L'IA rédige…" : "Suggérer le texte avec l'IA"}
              </button>
            </div>
            <input
              data-testid="campaign-goal-input"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Ex. Promouvoir mon annonce Market de cours de guitare"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Titre (max 80)</label>
            <input
              data-testid="campaign-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={80}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Texte (max 280)</label>
            <textarea
              data-testid="campaign-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={280}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Lien de destination (https://…)</label>
            <input
              data-testid="campaign-link-input"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://…"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy block mb-1">Emplacements</label>
            <div className="flex flex-wrap gap-3">
              {Object.entries(PLACEMENT_LABELS).map(([key, label]) => (
                <label key={key} className="inline-flex items-center gap-2 text-sm text-brand-navy">
                  <input
                    data-testid={`campaign-placement-${key}`}
                    type="checkbox"
                    checked={placements.includes(key)}
                    onChange={() => togglePlacement(key)}
                    className="rounded"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-brand-navy">Budget (CAD, 5 $ à 10 000 $)</label>
            <input
              data-testid="campaign-budget-input"
              type="number" min="5" max="10000" step="1"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
            />
            <p data-testid="campaign-impressions-preview" className="text-xs text-slate-400 mt-1">
              ≈ {impressions.toLocaleString("fr-CA")} impressions (1 $ = 100 impressions, budget fixe — aucune facturation au clic)
            </p>
          </div>
          <button
            data-testid="campaign-save-button"
            onClick={save}
            disabled={saving || placements.length === 0}
            className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50"
          >
            {saving ? "Création…" : "Créer la campagne (brouillon)"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Ads() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(false);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/ads/campaigns");
      setCampaigns(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const pay = async (campaign) => {
    setError("");
    setPayingId(campaign.campaign_id);
    try {
      const res = await api.post(`/ads/campaigns/${campaign.campaign_id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.approval_url;
    } catch (err) {
      setError(formatApiError(err));
      setPayingId(null);
    }
  };

  const action = async (campaign, verb) => {
    setError("");
    try {
      if (verb === "delete") await api.delete(`/ads/campaigns/${campaign.campaign_id}`);
      else await api.post(`/ads/campaigns/${campaign.campaign_id}/${verb}`);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!user) return null;

  return (
    <div data-testid="ads-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
              <Megaphone className="w-7 h-7 text-brand-turquoise" />
              NEXA Ads
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Faites la promotion de vos annonces, services ou offres auprès du réseau. Budget fixe en CAD, payé une seule fois par PayPal.
            </p>
          </div>
          <button
            data-testid="create-campaign-button"
            onClick={() => setDialog(true)}
            className="inline-flex items-center gap-2 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
          >
            <Plus className="w-4 h-4" /> Nouvelle campagne
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {error && (
          <div data-testid="ads-error" className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
          </div>
        ) : campaigns.length === 0 ? (
          <div data-testid="campaigns-empty" className="text-center py-16 text-slate-400 bg-white rounded-xl border border-slate-200">
            <Megaphone className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            Aucune campagne. Créez votre première campagne sponsorisée !
          </div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <div key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`} className="bg-white rounded-xl border border-slate-200 px-5 py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-medium text-brand-navy text-sm">{c.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {c.placements.map((p) => PLACEMENT_LABELS[p]).join(" · ")} — {c.budget} $ {c.currency} ({c.impressions_limit.toLocaleString("fr-CA")} impressions max)
                    </p>
                  </div>
                  <div className="text-xs text-slate-500 text-right">
                    <p data-testid={`campaign-impressions-${c.campaign_id}`}>{c.impressions_count} impressions</p>
                    <p data-testid={`campaign-clicks-${c.campaign_id}`}>{c.clicks_count} clics</p>
                  </div>
                  <span data-testid={`campaign-status-${c.campaign_id}`} className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_STYLES[c.status]}`}>
                    {STATUS_LABELS[c.status]}
                  </span>
                  <div className="flex items-center gap-2">
                    {!c.paid && (
                      <>
                        <button
                          data-testid={`campaign-pay-${c.campaign_id}`}
                          onClick={() => pay(c)}
                          disabled={payingId === c.campaign_id}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-blue rounded-lg px-3 py-1.5 hover:bg-brand-navy disabled:opacity-50"
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                          {payingId === c.campaign_id ? "Redirection…" : `Payer ${c.budget} $`}
                        </button>
                        <button
                          data-testid={`campaign-delete-${c.campaign_id}`}
                          onClick={() => action(c, "delete")}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Supprimer
                        </button>
                      </>
                    )}
                    {c.status === "active" && (
                      <button
                        data-testid={`campaign-pause-${c.campaign_id}`}
                        onClick={() => action(c, "pause")}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-100"
                      >
                        <Pause className="w-3.5 h-3.5" /> Pause
                      </button>
                    )}
                    {c.status === "en_pause" && (
                      <button
                        data-testid={`campaign-resume-${c.campaign_id}`}
                        onClick={() => action(c, "resume")}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy"
                      >
                        <Play className="w-3.5 h-3.5" /> Reprendre
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {dialog && <CampaignDialog onClose={() => setDialog(false)} onSaved={() => { setDialog(false); load(); }} />}
    </div>
  );
}
